import { ButtonVariant, CoButton } from "components/CoButton";
import { useBookmarks } from "features/Bookmarks";
import getQueryAndFilterStringForUrl from "features/FilterDrawer/utils/getQueryAndFilterStringForUrl";
import { IBookmarkItem, ISearchBookmark } from "helpers/bookmark";
import { getTimeAgoLabel } from "helpers/date";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import useConvertSearchBookmarkToThread from "../hooks/useConvertSearchBookmarkToThread";

type SearchBookmarkAsThreadProps = {
  bookmark: IBookmarkItem;
};

/**
 * @component SearchBookmarkAsThread
 * @description Show a search bookmark, but make it look like a thread when threads is enabled.
 *
 * Clicking on this bookmark uses the useConvertSearchBookmarkToThread hook to convert the search
 * bookmark into a thread bookmark before redirecting to it.
 * That way, the user won't notice that this was not actually a thread the whole time.
 */
function SearchBookmarkAsThread({ bookmark }: SearchBookmarkAsThreadProps) {
  const [labels] = useLabels("features.bookmark-lists.bookmark-list-detail");

  const bookmarkSearchData: ISearchBookmark =
    bookmark.bookmark_data as ISearchBookmark;

  const searchText = getQueryAndFilterStringForUrl(
    bookmarkSearchData.search_url
  );

  const { convertSearchBookmarkToThread } =
    useConvertSearchBookmarkToThread(bookmark);

  const { deleteBookmarkItem } = useBookmarks();

  const onUnbookmark = () => {
    deleteBookmarkItem(bookmark);
  };

  return (
    <div className="border-b border-border-base pt-4 pb-1.5">
      <button onClick={convertSearchBookmarkToThread}>
        <h3 className="base-md">{searchText}</h3>
      </button>
      <div className="flex items-center justify-between">
        <div className="tiny-normal flex gap-1.5 text-fg-muted">
          <span>
            <span className="mr-1 text-fg-base tiny-md">1</span>
            {pluralize(labels["queries"], {
              QUERY_COUNT: 1,
            })}
          </span>
          ·<span>{getTimeAgoLabel(bookmark.created_at)}</span>·
          <span className="inline-flex items-center gap-1.5">
            <i className="text-sm icon-eye" />
            {labels["shared"]}
          </span>
        </div>
        <CoButton
          variant={ButtonVariant.Flat}
          size="sm"
          className="px-2 md:px-4"
          onClick={onUnbookmark}
          icon="icon-bookmark-solid"
        />
      </div>
    </div>
  );
}

export default SearchBookmarkAsThread;
